import gql from 'graphql-tag';
import { useQuery, useResult, useMutation } from '@vue/apollo-composable';
import { useProfileInfo } from '@models/profile/useCurrentProfile';
import { ref, watch } from 'vue';
import { cloneDeep } from 'lodash-es';
import {
    EngageV4,
    EngageV4ListMember,
    EngageV4VersionListMember,
    EngageV4ListVersion,
    EngageV4TypeEnum,
    Usergroup,
    EngageV4Version,
    Goal,
    GoalMember,
    EngageV4StopOtherVersionTrafficInput
} from './graphql.schema';
import { ExperienceVersion } from './experience';
import { sortBy } from 'lodash-es';
import { toThousand } from '@utils/common.util';
import engageV4ListApi from '@experience/apis/engageV4List.api';

export const experienceListConditions = ref();
export const canSaveListConditions = ref(true);

export type ExperienceStatusType = 'PAUSE' | 'SCHEDULED' | 'DRAFT' | 'RUNNING' | 'UNSCHEDULED';

export type ExperienceListInfoType = {
    id: string;
    sid?: string;
    name?: string;
    status?: any;
    createTime?: string;
    updateTime?: string;
    lastPauseTime?: string;
    firstRunTime?: string;
    firstRunABTestTime?: string;
    hasForm?: boolean;
    hasSync?: boolean;
    isVersionEvent?: boolean;
    createBy?: string;
    modifierBy?: string;
    hasVariant?: boolean;
    engageVersion?: string;
    engageType: EngageV4TypeEnum;
    baseURL?: string;
    screenshot?: string;
    enableABTest?: boolean;
    schedule?: JSON;
    conditions?: JSON;
    usergroups?: Usergroup[];
    versions?: ExperienceVersion[];
    goals?: Goal[];
    count?: number;
    engageView?: number | string;
    engageViewCount?: number | string;
    engageClick?: number | string;
    engageClose?: number | string;
    engageSubmit?: number | string;
    engageClickRate?: number | string;
    engageCloseRate?: number | string;
    engageSubmitRate?: string | number;
    goal?: GoalMember[];
    avgSessionDuration?: number;
    bounceRate?: string;
    sessionViewDepth?: string;
    thumbnail?: string;
    defaultGoal:
        | GoalMember
        | {
              name: string;
              number: string;
              rate: string;
          };
    activeGoalNumber?: string | number;
    activeGoalRate?: string | number;
    activeGoalName?: string | number;
    sourceView?: string | number;
    sourceViewCount?: string | number;
    sourceClick?: string | number;
    sourceClose?: string | number;
    sourceSubmit?: string | number;
    antiFlicker?: boolean;
};

export type UpdateEngageV4Input = {
    id: string;
    name?: string;
    status: ExperienceStatusType;
    antiFlicker?: boolean;
};

const userGroup = `{
    type
    value
}`;

const Goal = `{
    id
    sid
    name
    content
    description
    createTime
    updateTime
    createBy
    modifierBy
    status
    count
}`;

const EngageV4CreativeTerminal = `{
    id
    type
    status
    popup
    stickybar
    inline
    code
    redirect
    engageVersionID
}`;

const EngageV4Version = `{
    id
    name
    rate
    controlGroup
    screenshot
    engageID
    terminals ${EngageV4CreativeTerminal}
}`;

const engageConfig = `{
    antiFlicker
    id
    sid
    name
    status
    createTime
    updateTime
    lastPauseTime
    firstRunTime
    hasForm
    hasSync
    isVersionEvent
    createBy
    modifierBy
    hasVariant
    engageVersion
    engageType
    baseURL
    screenshot
    schedule
    conditions
    usergroups ${userGroup}
    versions ${EngageV4Version}
    firstRunABTestTime
    enableABTest
    count
}`;

const np_package_info = `{
    sid
    ptxPackageId
    ptiPackageId
    startTime
    endTime
    resetTime
    ptxPrivilege {
        monthlyCampaignViewUsers
        campaigns
        userGroups
        usePopupForm
        useCampaignAbTest
        useCampaignUserGroupAndSession
        useCampaignCodeMode
        useCampaignJSTrigger
        useCampaignHeatmap
        useCampaignAI
        usePopup
        useStickyBar
        useInline
        useCampaignReport
        useCampaignReportCSVDownload
        useCampaignSettingPage
        usePopupStickyBarTrigger
        useCampaignSchedule
        useCampaignGoal
    }
    ptxPrivilegeUsage {
        monthlyCampaignViewUsers
        campaigns
        userGroups
    }
    ptiPrivilege {
        heatmaps
        HeatmapStorageMonths
        historicalHeatmaps
        useSegmentation
        useHeatmapCompetitor
        useSharableHeatmap
        useFunnel
        useHeatmapAbTest
        useHeatmap
        useRealtimeTracking
        useDataInsightsReport
        usePageGroup
        useConversionTracking
        useCampaignManagement
        useBasicSegmentation
    }
    ptiPrivilegeUsage {
        heatmaps
        historicalHeatmaps
    }
    ptiFreeTrial
    ptxFreeTrial
    trialEndTime
    bindWay
}`;

// graphql - 套餐查询
const GET_NP_PACKAGE_QUERY = gql`
    query getProfileNpPackageInfo($profileId: String!) {
        getProfileNpPackageInfo(profileId: $profileId)${np_package_info}
    }
`;

// graphql - 暂停其他版本
const PAUSE_OTHER_VERSION_MUTATION = gql`
    mutation stopOtherVersionTraffic($input: EngageV4StopOtherVersionTrafficInput!) {
        stopOtherVersionTraffic(input: $input)
    }
`;

export const experienceConfigList = ref<{ sid: string; configList: EngageV4[] | null }>({
    sid: '',
    configList: []
});
export const experienceConfigListLoading = ref(false);
export const experienceList = ref<EngageV4ListMember[] | undefined>();
export const experienceListLoading = ref(true);
export const experienceABTestList = ref<EngageV4VersionListMember[] | undefined>();
export const experienceABTestListLoading = ref(true);

// 清除列表缓存数据
export function clearExperienceList() {
    experienceConfigList.value = {
        sid: '',
        configList: []
    };
    experienceConfigListLoading.value = true;
    experienceList.value = undefined;
    experienceListLoading.value = true;
    experienceABTestList.value = undefined;
    experienceABTestListLoading.value = true;
}

// 获取配置列表
export async function queryExperienceConfigList(
    profileId: string,
    source:
        | 'profileSelectChange' //顶部档案切换
        | 'enterEngagementRouter' //路由进入前
        | 'experienceListOnMounted' //列表页初始化
        | 'experienceListWatchSidChange' //列表页监听到档案切换
        | 'experienceOnboardingOnMounted' //Onboarding初始化
) {
    if (!profileId) {
        experienceConfigList.value.sid = '';
        experienceConfigList.value.configList = null;
        return Promise.reject();
    }

    // 非切换档案操作，且已存在数据。则直接使用缓存数据
    // const { sid, configList } = experienceConfigList.value;
    // if (
    //     sid === profileId &&
    //     configList &&
    //     ['experienceListOnMounted', 'enterEngagementRouter'].includes(source)
    // ) {
    //     return Promise.resolve(configList);
    // }

    experienceConfigListLoading.value = true;

    return engageV4ListApi
        .queryEngageV4ConfigList({ profileId })
        .then(
            (res: EngageV4[]) => {
                experienceConfigList.value.sid = profileId;
                experienceConfigList.value.configList = res?.map((config: EngageV4) => {
                    return transformListData(config, 'experienceConfig');
                });
                return Promise.resolve(res);
            },
            err => {
                experienceConfigList.value.sid = '';
                experienceConfigList.value.configList = null;
                return Promise.reject(err);
            }
        )
        .finally(() => {
            experienceConfigListLoading.value = false;
        });
}

// 获取列表
export function queryExperienceList(sid: string) {
    if (!sid || sid !== experienceConfigList.value.sid) {
        return Promise.reject();
    }

    experienceListLoading.value = true;

    return engageV4ListApi
        .queryEngageV4List()
        .then(
            res => {
                // 此接口返回的配置不对，需同步配置列表信息
                experienceList.value = res?.reduce(
                    (acc: EngageV4ListMember[], cur: EngageV4ListMember) => {
                        const config = experienceConfigList.value.configList?.find(
                            item => item.id === cur.engageConfig?.id
                        );
                        if (config) {
                            const _item = { ...config, ...cur.engageData };

                            // versions列表采用接口返回的数据
                            // if (cur.engageConfig?.versions) {
                            //     _item.versions = cur.engageConfig?.versions;
                            // }
                            acc.push(transformListData(_item, 'experienceList'));
                        }
                        return acc;
                    },
                    []
                );
                return Promise.resolve(res);
            },
            err => {
                experienceList.value = undefined;
                return Promise.reject(err);
            }
        )
        .finally(() => {
            experienceListLoading.value = false;
        });
}

// experienceAB测列表
export function queryExperienceAbList(sid: string) {
    if (!sid || sid !== experienceConfigList.value.sid) {
        return Promise.reject();
    }

    experienceABTestListLoading.value = true;

    return engageV4ListApi
        .queryEngageV4VersionList()
        .then(
            res => {
                // 此接口返回的配置不对，需同步配置列表信息
                experienceABTestList.value = res?.reduce(
                    (acc: EngageV4VersionListMember[], cur: EngageV4VersionListMember) => {
                        const config = experienceConfigList.value.configList?.find(
                            item => item.id === cur.id
                        );
                        if (config) {
                            cur.versions = sortBy(
                                cur.versions,
                                function (version: EngageV4ListVersion) {
                                    return !version.controlGroup;
                                }
                            );
                            // versions列表采用ABTestList接口返回的数据
                            acc.push({
                                ...cur,
                                ...config,
                                ...cur.engageData,
                                versions: cur.versions
                            });
                        }
                        return acc;
                    },
                    []
                );
                return Promise.resolve(experienceABTestList.value);
            },
            err => {
                experienceABTestList.value = undefined;
                return Promise.reject(err);
            }
        )
        .finally(() => {
            experienceABTestListLoading.value = false;
        });
}

// 更新AB测列表信息
export function updateAbListByManual(id: string, key: any, value: any) {
    const item = experienceABTestList?.value?.find((item: any) => item.id === id) as any;
    if (item) {
        item[key] = value;
    }
}

// 列表中删除experience
export function deleteExperienceFromList(id: string) {
    if (!id) return;

    [
        experienceConfigList.value?.configList,
        experienceList.value,
        experienceABTestList.value
    ].forEach(list => {
        if (!list) return;

        const index = list.findIndex((engage: any) => engage.id === id);
        if (index !== undefined && index > -1) {
            list.splice(index, 1);
        }
    });
}

// 更新列表中某一条experience信息
export function updateExperienceFromList(experienceId: string, attributeParam: any) {
    if (!experienceId) return;

    [
        experienceConfigList.value?.configList,
        experienceList.value,
        experienceABTestList.value
    ].forEach(list => {
        if (!list) return;

        const engage = list.find((engage: any) => engage.id === experienceId);
        if (!engage) return;

        engage.updateTime = new Date().getTime();
        Object.keys(attributeParam).map(key => {
            if (key === 'status') {
                const experienceStatus = attributeParam[key];
                if (experienceStatus === 'RUNNING' && !engage.firstRunTime) {
                    engage.firstRunTime = new Date().getTime();
                }
                if (experienceStatus === 'PAUSE') {
                    engage.lastPauseTime = new Date().getTime();
                    engage.status =
                        engage.status === 'SCHEDULED' && !engage.firstRunTime ? 'DRAFT' : 'PAUSE';
                } else {
                    engage.status = experienceStatus;
                }
            } else {
                engage[key] = attributeParam[key];
            }
        });
    });
}

export function updateExperienceListByManual(id: string, key: any, value: any) {
    const item = experienceList?.value?.find((item: any) => item.id === id) as any;
    if (item) {
        item[key] = value;
    }
}

// 暂停其他版本
export function pauseOtherVersions(input: EngageV4StopOtherVersionTrafficInput) {
    const { mutate: stopOtherVersionMutate } = useMutation(PAUSE_OTHER_VERSION_MUTATION);

    return stopOtherVersionMutate(
        { input },
        {
            update: (cache, { data: { stopOtherVersionTraffic } }) => {
                if (!stopOtherVersionTraffic) return;
                // 更新AB侧流量分配
                const _index: number | undefined = experienceABTestList.value?.findIndex(
                    (engage: EngageV4VersionListMember) => {
                        return engage.id === input.id;
                    }
                );
                if (_index !== undefined && _index > -1 && experienceABTestList.value?.[_index]) {
                    experienceABTestList.value[_index].versions = experienceABTestList.value[
                        _index
                    ].versions?.map((version: EngageV4ListVersion) => {
                        version.rate = version.id === input.versionId ? 1 : 0;
                        return version;
                    });
                }
            }
        }
    ).then((result: any) => {
        return result.data.stopOtherVersionTraffic;
    });
}

// 获取套餐信息
export function getNpPackageInfo(profileId: string | undefined) {
    const {
        result,
        refetch: refetchNpPackage,
        loading: npPackageInfoLoading,
        onResult: onNpPackageResult
    } = useQuery<{ getProfileNpPackageInfo: any }>(GET_NP_PACKAGE_QUERY, {
        profileId
    });
    const npPackageInfo = useResult(result, null, data => {
        return data?.getProfileNpPackageInfo;
    });
    return {
        npPackageInfo,
        refetchNpPackage,
        npPackageInfoLoading,
        onNpPackageResult
    };
}

export const initExperienceListConditions = () => {
    canSaveListConditions.value = true;
};

export const setExperienceListConditions = (conditions: any) => {
    if (canSaveListConditions.value) {
        experienceListConditions.value = {
            ...experienceListConditions.value,
            ...cloneDeep(conditions)
        };
    }
};

export const clearExperienceListConditions = () => {
    canSaveListConditions.value = false;
    experienceListConditions.value = null;
};

/**
 * 格式化表单数据
 * 仅配置时，数据部分为null
 */
function transformListData(_item: any, source: 'experienceConfig' | 'experienceList') {
    if (source === 'experienceConfig') {
        return {
            ..._item,
            thumbnail: _item.screenshot,
            bounceRate: 'null',
            sessionViewDepth: 'null',
            engageSubmitRate: 'null',
            engageClickRate: 'null',
            engageCloseRate: 'null',
            sourceView: 'null',
            engageView: 'null',
            sourceViewCount: 'null',
            engageViewCount: 'null',
            sourceClick: 'null',
            engageClick: 'null',
            sourceClose: 'null',
            engageClose: 'null',
            sourceSubmit: 'null',
            engageSubmit: 'null',
            activeGoalNumber: 'null',
            activeGoalRate: 'null',
            activeGoalName: 'null',
            defaultGoal: 'null',
            avgSessionDuration: 'null',
            goalNumber: 'null',
            goalRate: 'null',
            lastRunningPeriod: 'null'
        };
    }

    return {
        ..._item,
        thumbnail: _item.screenshot,
        bounceRate:
            _item.status !== 'DRAFT' ? ((_item.bounceRate ?? 0) * 100)?.toFixed(2) + '%' : '--',
        sessionViewDepth:
            _item.status !== 'DRAFT' ? (_item.sessionViewDepth ?? 0).toFixed(2) : '--',
        engageSubmitRate:
            _item.status !== 'DRAFT'
                ? ((_item.engageSubmitRate ?? 0) * 100)?.toFixed(2) + '%'
                : '--',
        engageClickRate:
            _item.status !== 'DRAFT'
                ? ((_item.engageClickRate ?? 0) * 100)?.toFixed(2) + '%'
                : '--',
        engageCloseRate:
            _item.status !== 'DRAFT'
                ? ((_item.engageCloseRate ?? 0) * 100)?.toFixed(2) + '%'
                : '--',
        sourceView: _item.engageView,
        engageView:
            _item.status !== 'DRAFT' ? _item.engageView && toThousand(_item.engageView) : '--',
        sourceViewCount: _item.engageViewCount,
        engageViewCount:
            _item.status !== 'DRAFT'
                ? _item.engageViewCount && toThousand(_item.engageViewCount)
                : '--',
        sourceClick: _item.engageClick,
        engageClick:
            _item.status !== 'DRAFT' ? _item.engageClick && toThousand(_item.engageClick) : '--',
        sourceClose: _item.engageClose,
        engageClose:
            _item.status !== 'DRAFT' ? _item.engageClose && toThousand(_item.engageClose) : '--',
        sourceSubmit: _item.engageSubmit,
        engageSubmit:
            _item.status !== 'DRAFT' ? _item.engageSubmit && toThousand(_item.engageSubmit) : '--',
        activeGoalNumber: _item.goal && _item.goal.length ? _item.goal[0]?.number : '--',
        activeGoalRate: _item.goal && _item.goal.length ? _item.goal[0]?.rate : '--',
        activeGoalName: _item.goal && _item.goal.length ? _item.goal[0]?.name : '--',
        defaultGoal:
            _item.goal && _item.goal.length
                ? _item.goal[0]
                : { name: '--', number: '--', rate: '--' }
    };
}
